import { useEffect } from 'react';
import SEO from '../../../components/SEO';

export default function CommercialSecurityHamilton() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const faqs = [
    {
      question: "What security challenges do Hamilton industrial businesses face?",
      answer: "Hamilton's steel and manufacturing sector faces equipment theft, metal theft, and vandalism. Industrial facilities in Red Hill, Stoney Creek, and the waterfront require perimeter security with thermal cameras, motion sensors, and 24/7 monitoring. The city's industrial heritage means many facilities operate in older buildings requiring security retrofits and modernization."
    },
    {
      question: "How does Hamilton's downtown revitalization affect security needs?",
      answer: "Downtown Hamilton's growing restaurant, retail, and office sectors require modern security systems to protect new investments. James Street North, King William, and Locke Street businesses need HD cameras, alarm systems, and access control to prevent break-ins while maintaining the area's historic character with discreet installations."
    },
    {
      question: "What's the cost of commercial security in Hamilton?",
      answer: "Hamilton commercial security typically ranges from $1,800-$6,500 for installation plus $35-$120/month for monitoring. Downtown retail averages $2,500-$4,500, while large industrial facilities in Red Hill or Stoney Creek invest $5,000-$12,000 for comprehensive perimeter protection, equipment monitoring, and metal theft prevention systems."
    }
  ];

  const neighborhoods = [
    "Downtown Hamilton",
    "James Street North",
    "Locke Street",
    "King William District",
    "Red Hill Business Park",
    "Stoney Creek Industrial",
    "Waterfront District",
    "Ancaster Business Park",
    "Upper James",
    "Dundas",
    "West Harbour",
    "Eastgate Square Area"
  ];

  const otherCities = [
    { name: "Toronto", slug: "toronto" },
    { name: "Mississauga", slug: "mississauga" },
    { name: "Brampton", slug: "brampton" },
    { name: "Oakville", slug: "oakville" },
    { name: "Richmond Hill", slug: "richmond-hill" },
    { name: "Vaughan", slug: "vaughan" }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://alliancesecuritysystems.com/commercial-security/hamilton#business",
        "name": "Alliance Security Systems - Commercial Security Hamilton",
        "image": "https://public.readdy.ai/ai/img_res/19250358973ac5ad4f435f6f3c9b1835.jpg",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Hamilton",
          "addressRegion": "ON",
          "addressCountry": "CA"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "43.2557",
          "longitude": "-79.8711"
        },
        "url": "https://alliancesecuritysystems.com/commercial-security/hamilton",
        "telephone": "+1-888-458-9181",
        "priceRange": "$$-$$$",
        "areaServed": {
          "@type": "City",
          "name": "Hamilton"
        },
        "serviceType": ["Commercial Security Systems", "Business Alarm Systems", "Commercial CCTV", "Access Control Systems"]
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://alliancesecuritysystems.com/commercial-security/hamilton#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://alliancesecuritysystems.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Commercial Security",
            "item": "https://alliancesecuritysystems.com/commercial-security"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Hamilton",
            "item": "https://alliancesecuritysystems.com/commercial-security/hamilton"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "@id": "https://alliancesecuritysystems.com/commercial-security/hamilton#faq",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Commercial Security Hamilton | Business Alarm & Camera Systems"
        description="Professional commercial security systems in Hamilton. ULC-certified monitoring, HD cameras, access control for industrial, retail & downtown businesses. Trusted by 130+ Hamilton companies."
        keywords="commercial security Hamilton, business alarm system Hamilton, commercial CCTV Hamilton, access control Hamilton, Hamilton business security"
        canonical="/commercial-security/hamilton"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
          <img 
            src="https://readdy.ai/api/search-image?query=Hamilton%20Ontario%20downtown%20skyline%20industrial%20waterfront%20steel%20city%20modern%20buildings%20business%20district%20aerial%20view%20professional%20cityscape%20evening%20lighting%20urban%20renewal&width=1920&height=800&seq=comm-hamilton-hero&orientation=landscape"
            alt="Commercial Security Hamilton - Downtown Business District"
            title="Hamilton Commercial Security Systems"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
          
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
            <nav className="flex items-center justify-center gap-2 text-sm mb-6" itemScope itemType="https://schema.org/BreadcrumbList">
              <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <a href="/" itemProp="item" className="hover:text-teal-400 transition-colors">
                  <span itemProp="name">Home</span>
                </a>
                <meta itemProp="position" content="1" />
              </span>
              <span className="text-white/60">/</span>
              <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <a href="/commercial-security" itemProp="item" className="hover:text-teal-400 transition-colors">
                  <span itemProp="name">Commercial Security</span>
                </a>
                <meta itemProp="position" content="2" />
              </span>
              <span className="text-white/60">/</span>
              <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span itemProp="name" className="text-teal-400">Hamilton</span>
                <meta itemProp="position" content="3" />
              </span>
            </nav>

            <h1 className="text-5xl font-bold mb-6">Commercial Security Hamilton</h1>
            <p className="text-xl mb-8 text-white/90">Protecting The Ambitious City — Industrial-Grade Security for Manufacturing, Retail & Downtown Businesses</p>
            <button 
              onClick={scrollToForm}
              className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
            >
              Get Free Security Assessment
            </button>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-gray-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">130+</div>
                <div className="text-sm text-gray-300">Hamilton Commercial Clients</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">&lt;5 min</div>
                <div className="text-sm text-gray-300">Average Response Time</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">20+ yrs</div>
                <div className="text-sm text-gray-300">Serving Hamilton Businesses</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">23%</div>
                <div className="text-sm text-gray-300">Avg Insurance Discount</div>
              </div>
            </div>
          </div>
        </section>

        {/* Commercial Security Challenges in Hamilton */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Commercial Security Challenges in Hamilton</h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Ontario's steel city combines heavy industry, downtown revitalization, and growing commercial sectors with diverse security requirements
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-hammer-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Industrial Metal & Equipment Theft</h3>
                <p className="text-gray-600 mb-4">
                  Hamilton's manufacturing and steel sectors face persistent metal theft, equipment vandalism, and copper wire theft. Industrial facilities in Red Hill, Stoney Creek, and the waterfront require perimeter fencing with thermal cameras, motion sensors, and real-time alerts to prevent losses averaging $35,000 per incident.
                </p>
                <div className="text-sm text-gray-500">
                  <strong>Stat:</strong> Hamilton metal theft increased 47% in 2024, targeting copper and industrial equipment
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-building-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Downtown Revitalization Security</h3>
                <p className="text-gray-600 mb-4">
                  James Street North, King William, and Locke Street's growing restaurant and retail sectors require modern security protecting new investments while respecting historic architecture. Businesses need discreet HD cameras, smart alarms, and coordinated response with Hamilton Police Service to combat break-ins and vandalism.
                </p>
                <div className="text-sm text-gray-500">
                  <strong>Stat:</strong> Downtown Hamilton commercial break-ins decreased 31% in areas with professional security
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">24/7 Manufacturing Operations</h3>
                <p className="text-gray-600 mb-4">
                  Hamilton's manufacturing facilities operate around the clock, requiring continuous monitoring and rapid response capabilities. Shift changes, after-hours deliveries, and weekend operations demand integrated access control, employee tracking, and video verification to prevent internal theft and ensure worker safety.
                </p>
                <div className="text-sm text-gray-500">
                  <strong>Stat:</strong> 52% of Hamilton manufacturing theft occurs during shift changes and weekends
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Commercial Security Solutions for Hamilton</h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Robust security systems designed for Hamilton's industrial, retail, and downtown business sectors
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <a href="/camera-packages" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-camera-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Commercial Cameras</h3>
                <p className="text-gray-600 mb-4">4K resolution, thermal imaging, license plate recognition, AI analytics for perimeter monitoring and equipment protection</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              <a href="/alarm-packages" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-alarm-warning-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Business Alarms</h3>
                <p className="text-gray-600 mb-4">ULC-certified monitoring, perimeter sensors, glass break detection, panic buttons, direct Hamilton Police Service integration</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              <a href="/access-control" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-key-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Access Control</h3>
                <p className="text-gray-600 mb-4">Keycard systems, biometric readers, time-based access, employee tracking for manufacturing facilities and warehouses</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              <a href="/live-monitoring" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-eye-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Live Monitoring</h3>
                <p className="text-gray-600 mb-4">24/7 video verification, two-way audio intervention, real-time threat assessment, coordinated emergency response</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Neighborhoods Served */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Hamilton Business Districts We Serve</h2>
            <p className="text-center text-gray-600 mb-12">
              Protecting businesses across all Hamilton neighborhoods and industrial zones
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {neighborhoods.map((neighborhood, index) => (
                <div key={index} className="bg-gray-50 px-6 py-4 rounded-lg text-center hover:bg-teal-50 transition-colors">
                  <div className="font-semibold text-gray-800">{neighborhood}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Hamilton Commercial Security FAQs</h2>
            <p className="text-center text-gray-600 mb-12">
              Common questions from Hamilton business owners
            </p>

            <div className="space-y-6" itemScope itemType="https://schema.org/FAQPage">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white p-8 rounded-lg" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                  <h3 className="text-xl font-bold mb-3 flex items-start gap-3" itemProp="name">
                    <span className="text-teal-500 flex-shrink-0">Q:</span>
                    <span>{faq.question}</span>
                  </h3>
                  <div className="pl-8 text-gray-600" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p itemProp="text">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact-form" className="py-20 bg-white">
          <div className="max-w-2xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Get Your Free Hamilton Security Assessment</h2>
            <p className="text-center text-gray-600 mb-12">
              On-site evaluation, custom proposal, and transparent pricing — no obligation
            </p>

            <form className="space-y-6 bg-gray-50 p-8 rounded-lg" data-readdy-form id="hamilton-commercial-form" action="https://readdy.ai/api/form/d6ggqdmqpmcju3gsd8p0" method="POST">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Business Name</label>
                  <input 
                    type="text" 
                    name="business_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Your Company Ltd."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Your Name</label>
                  <input 
                    type="text" 
                    name="contact_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="John Smith"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Phone</label>
                  <input 
                    type="tel" 
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="905-XXX-XXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Email</label>
                  <input 
                    type="email" 
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Business Address in Hamilton</label>
                <input 
                  type="text" 
                  name="business_address"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="123 King St E, Hamilton, ON"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Industry</label>
                <select 
                  name="industry"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">Select Industry</option>
                  <option value="retail">Retail</option>
                  <option value="office">Office / Corporate</option>
                  <option value="warehouse">Warehouse / Logistics</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="hospitality">Hospitality</option>
                  <option value="construction">Construction</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Security Needs (optional)</label>
                <textarea 
                  name="security_details"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Describe your security concerns, current system, or specific requirements..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1">Maximum 500 characters</div>
              </div>

              <button 
                type="submit"
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Free Assessment
              </button>
            </form>
          </div>
        </section>

        {/* Other Cities */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-12">Commercial Security in Other GTA Cities</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
              {otherCities.map((city, index) => (
                <a 
                  key={index}
                  href={`/commercial-security/${city.slug}`}
                  className="bg-white px-6 py-4 rounded-lg text-center hover:shadow-lg transition-shadow cursor-pointer group"
                >
                  <div className="font-semibold text-gray-800 group-hover:text-teal-600 transition-colors">{city.name}</div>
                </a>
              ))}
            </div>
            <div className="text-center mt-8">
              <a href="/commercial-security/service-areas" className="text-teal-600 font-semibold hover:text-teal-700 inline-flex items-center gap-2">
                View All Service Areas <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* CTA Strip */}
        <section className="bg-gradient-to-r from-teal-600 to-teal-700 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Hamilton Business?</h2>
            <p className="text-xl mb-8 text-white/90">
              Join 130+ Hamilton companies trusting us with their security
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button 
                onClick={scrollToForm}
                className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Assessment
              </button>
              <a 
                href="/commercial-security"
                className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-block"
              >
                View All Solutions
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}